/**
 * 반품작업 2.0 (rgr06, 라우트 rgr17) — 컴포넌트 모드 엔트리(문서 51 · 가이드 52).
 *
 * 화면은 `handoff/page.generated.tsx`(컨덕터 생성본 — 재핸드오프 때 통째로 교체)가 그리고, 이 파일은 그 뷰의 Wiring 슬롯에
 * 옛 배선(`hooks/useReturnWorkScan` — HEAD·ITEM 조회·저장 · `hooks/useInspectionRecorder` — 웹캠 녹화 · `fileUpload` — S3 업로드)을 잇는다.
 * UI 를 여기서 그리지 않는다.
 *
 * 규칙(가이드 52 §5-A): 실패 = throw(모달 유지), false = 성공인데 뷰 토스트 생략(훅이 결과를 알린다 — 스캔 화면은 알럿 없이 scanNotice 큰 토스트, notice.tsx).
 *
 * PD 결정(2026-09-30):
 *  - 주문검색 = 라디오 + [선택] — 고른 주문으로 송장 정보를 채운다(옛 applyOrderSearchResult). 출고일자(단일 날짜)를 넣으면 그날로,
 *    비워 두면 최근 ORDER_SEARCH_PERIOD_MONTHS 개월로 찾는다(BE 는 기간이 필수). 날짜 옆 유형(일/월/직접입력)은 뷰가 값을 넘기지 않아 쓰지 않는다.
 *  - [합포 불러오기]·[바코드 인쇄]는 RGR11 전용이라 이 화면에 없다(dev D15 와 같다).
 *  - 수기입력사유는 수기입력 모드의 7번째 칸(JOB_COMMENT).
 *  - **검수 자동녹화**(2.0 개선 — 1.0 은 버튼으로만 녹화) — 송장 정보가 채워지는 순간(원송장 조회 성공·주문검색 선택) 또는 수기입력을 켠 순간
 *    녹화를 시작하고, 검수완료 저장이 성공하면 멈춰 녹화본을 저장된 행에 올린다(rgr11 개발본 useMediaCapture 와 같은 시작 시점). 녹화 버튼은 없다.
 *  - **카메라 미리보기·촬영**(10-01, DS 수정 없이) — 뷰의 녹화 슬롯(`recording.rgr06Recording`, 컨덕터 musinsa/pbo-design#688)에 녹화 훅의
 *    미리보기 `<video>`·녹화 중 표시·촬영 목록(capture_YYYYMMDDHHmmss, x 로 한 장 지우기)을 잇는다. 촬영분은 검수완료 때 녹화본과 함께 올라간다.
 *
 * 뷰가 못 하는 것(TODO(dev) — PR 본문에도 적는다):
 *  - 중복 반품번호 행 빨간 배경: 표 행 스타일 슬롯이 계약에 없다.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReturnShipperDisplay, CoMingleReturnProps } from '@/pages/return/coMingle'
import { toReturnShipper } from '@/pages/return/coMingle'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useGetReturnOrderSearch } from '@/services/return/returnWorkScan/useReturnWorkScanQueries'
import {
  CONDITION_OPTIONS,
  ConditionValue,
  ReturnOrderSearchFilter,
  ReturnOrderSearchRes,
  ReturnWorkScanHead,
  ReturnWorkScanRow,
  ReturnWorkScanSaveRes,
} from '@/services/return/returnWorkScan/types'
import { INSPECTION_DEFAULTS, MASTER_FLAG_OPTIONS, MESSAGES, ORDER_SEARCH_PERIOD_MONTHS, ReturnWorkFilters } from './constants'
import { uploadInspectionFiles } from './fileUpload'
import { scanInvalidError, scanNotice } from '@/components/pbo/notice'
import { useInspectionRecorder } from './hooks/useInspectionRecorder'
import { useReturnWorkScan } from './hooks/useReturnWorkScan'
import { fromSearchQuery, ReturnWorkSearchQuery, toSearchQuery } from './searchQuery'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnWorkPageView, { type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

// 출고일자('YYYY-MM-DD') 엄격 파싱 — 플러그인 없이는 형식 인자를 무시하고 느슨하게 읽는다(stockMoveHistory/searchQuery.ts 와 같다).
dayjs.extend(customParseFormat)

/** 뷰의 송장 정보 항목 key(데모 데이터 필드) ↔ 훅 HEAD 필드. 송장(TRS_INVOICE_NO)은 원송장 값이다. */
const HEAD_FIELD_BY_KEY: Record<string, keyof ReturnWorkScanHead> = {
  ORD_NM: 'orderName',
  TRS_NM: 'receiverName',
  TRS_PHONE: 'receiverPhone',
  TRS_ZIP: 'receiverZip',
  TRS_ADDR: 'receiverAddress',
}
const INVOICE_KEY = 'TRS_INVOICE_NO'
const JOB_COMMENT_KEY = 'JOB_COMMENT'

/** 선택지 — 모듈 상수(같은 참조). options 슬롯은 매 렌더 불리므로 거기서 새 배열을 만들지 않는다(사전 리뷰 S1). */
const MASTER_FLAG_VIEW_OPTIONS = MASTER_FLAG_OPTIONS.map((o) => ({ label: o.label, value: o.value }))
const CONDITION_VIEW_OPTIONS = CONDITION_OPTIONS.map((o) => ({ label: o.label, value: o.value }))

const text = (v: unknown): string => {
  if (typeof v === 'string') return v
  if (typeof v === 'number') return String(v)
  return ''
}

/** 스캔 행 → 뷰 표 행(데모 컬럼 키). key 는 훅 행 key — 행을 더해도 체크 선택이 같은 행에 붙는다. */
export const toViewRow = (r: ReturnWorkScanRow): Row => ({
  key: r.key,
  ORD_NO: r.orderNumber,
  RL_CHANNEL: r.rlChannel,
  BARCODE: r.barcode,
  SKU_ID: r.skuId,
  GDS_CD: r.goodsCode,
  GDS_NM: r.goodsName,
  GDS_OPT: r.goodsOption,
  ORD_QTY: r.orderQuantity,
  ETC5: r.condition,
  PROC_NM: r.procurementName,
  REF_NO1: r.referenceNumber1,
  SAL_CHANNEL: r.salesChannel,
  BRD_NM: r.brandName,
  TRS_LSP_NM: r.logisticsProviderName,
  ETC1: r.etc1,
  ETC2: r.etc2,
  ETC3: r.etc3,
  ETC4: r.etc4,
  INS_DATE: r.insDate,
  INS_BY: r.insBy,
  UPD_DATE: r.updDate,
  UPD_BY: r.updBy,
})

/**
 * 편집된 뷰 행 → 훅 행(편집 셀 9종만 되돌린다 — 나머지는 조회 값이라 표시용이다). 원본이 없는 key 는 버린다.
 * 반품번호는 1.0 처럼 작업자가 고칠 수 있다 — 수기입력은 HEAD 조회가 없어 1부터 매긴다(LOGISPRD-14907). 번호를 바꾼 행은 중복 표시를 푼다.
 */
export const fromViewRows = (next: Row[], byKey: Map<string, ReturnWorkScanRow>): ReturnWorkScanRow[] =>
  next.flatMap((v) => {
    const orig = byKey.get(text(v.key))
    if (!orig) return []
    const condition = text(v.ETC5)
    const known = CONDITION_OPTIONS.some((o) => o.value === condition)
    const orderNumber = text(v.ORD_NO).trim()
    return [
      {
        ...orig,
        orderNumber,
        duplicate: orig.duplicate && orderNumber === orig.orderNumber,
        rlChannel: text(v.RL_CHANNEL),
        condition: known ? (condition as ConditionValue) : '',
        procurementName: text(v.PROC_NM),
        logisticsProviderName: text(v.TRS_LSP_NM),
        etc1: text(v.ETC1),
        etc2: text(v.ETC2),
        etc3: text(v.ETC3),
        etc4: text(v.ETC4),
      },
    ]
  })

/** 'YYYYMMDD'(BE 응답) → 'YYYY-MM-DD'(데모 표기). 형식이 다르면 그대로 둔다. */
export const toDashedDate = (v: string | null): string => (v && /^\d{8}$/.test(v) ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6)}` : (v ?? ''))

/** 주문검색 응답 → 뷰 후보 표 행. key 는 옛 모달 rowKey 와 같다(송장·SKU·출고일자). 출고일자는 데모와 같게 YYYY-MM-DD 로 보인다. */
export const orderRowKey = (r: ReturnOrderSearchRes): string => `${r.trsInvoiceNo ?? ''}-${r.barcode ?? ''}-${r.actDate ?? ''}`
export const toOrderViewRow = (r: ReturnOrderSearchRes & ReturnShipperDisplay, coMingleEnabled = false): Row => ({
  key: orderRowKey(r),
  SPR_NM: (coMingleEnabled ? r.shipperName : null) ?? r.sprNm ?? '',
  GI_DATE: toDashedDate(r.actDate),
  TRS_INVOICE_NO: r.trsInvoiceNo ?? '',
  ORD_NM: r.ordNm ?? '',
  TRS_NM: r.trsNm ?? '',
  TRS_PHONE: r.trsMobile ?? '',
  TRS_TEL: r.trsPhone ?? '',
  TRS_ZIP: r.trsZip ?? '',
  TRS_ADDR: r.trsAddr ?? '',
  BARCODE: r.barcode ?? '',
  ORD_QTY: r.actQty ?? '',
  REF_NO1: r.refNo1 ?? '',
  SAL_CHANNEL: r.salChannel ?? '',
  BRD_NM: r.brdNm ?? '',
})

/**
 * 모달 [조회]가 적용한 조건 → 주문검색 요청. 누르기 전(`__applied` 없음)은 null(조회 안 함).
 * 출고일자(GI_DATE, 'YYYY-MM-DD')를 넣었으면 그날 하루, 비웠으면 최근 ORDER_SEARCH_PERIOD_MONTHS 개월 — BE 는 기간이 필수다.
 */
export const toOrderSearchFilter = (applied: Record<string, unknown> | undefined, shipperCode: string, today = dayjs(), coMingleEnabled = false): ReturnOrderSearchFilter | null => {
  if (!applied?.__applied) return null
  const one = (k: string) => text(applied[k]).trim() || undefined
  const list = (k: string) => {
    const v = one(k)
    return v ? [v] : undefined
  }
  const day = dayjs(text(applied.GI_DATE), 'YYYY-MM-DD', true)
  const from = day.isValid() ? day : today.subtract(ORDER_SEARCH_PERIOD_MONTHS, 'month')
  const to = day.isValid() ? day : today
  return {
    actDateFrom: from.format('YYYYMMDD'),
    actDateTo: to.format('YYYYMMDD'),
    shipper: toReturnShipper(one('SPR_NM') || shipperCode, coMingleEnabled) || undefined, // 모달 화주 칸을 비우면 현재 화주 — 허용 화주 제한이 없는 계정이 전 화주를 긁지 않게(LOGISPRD-14897)
    barcode: list('BARCODE'),
    refNo1: list('REF_NO1'),
    trsInvoiceNo: one('TRS_INVOICE_NO'),
    ordNm: one('ORD_NM'),
    trsNm: one('TRS_NM'),
    trsPhone: one('TRS_PHONE'),
    trsAddr: one('TRS_ADDR'),
  }
}

export default function ReturnWorkPage({ coMingleEnabled = false }: Partial<CoMingleReturnProps>) {
  // 기준은 현재 화주(shipperCode, 화주 전환 반영)다 — 1.0 은 전환된 userInfo.company 를 보냈다. 비전환 상태에서는 SPR_NM 과 같다(LOGISPRD-14358).
  const shipperCode = useMemo(() => {
    const user = AuthStorage.user.get()
    return user.shipperCode || user.SPR_NM || ''
  }, [])

  // ── 조회조건(마스터 조회여부) — 메뉴 세션·URL 쿼리에 남긴다(D16). 복원은 URL 에서, 마운트 때 한 번 ──
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<ReturnWorkSearchQuery>()
  const [filters, setFilters] = useState<ReturnWorkFilters>(() => fromSearchQuery(parsedQs as Partial<ReturnWorkSearchQuery>))
  const updateFilters = useCallback(
    (next: ReturnWorkFilters) => {
      const searchQuery = toSearchQuery(next)
      MenuStorage.menuOnSession.setSearch(searchQuery)
      reload()
      replaceQuery(searchQuery)
      setFilters(next)
    },
    [reload, replaceQuery],
  )

  // ── 검수 녹화 — 카메라는 진입 때 켜 두고(autoRecord 는 끔 — 진입 즉시가 아니라 검수가 시작될 때 녹화), 아래 트리거로 시작한다 ──
  const recorder = useInspectionRecorder({
    autoRecord: INSPECTION_DEFAULTS.autoRecord,
    recordTimeLimitSeconds: INSPECTION_DEFAULTS.recordTimeLimitSeconds,
  })
  const { captures, discard: discardRecorder, stopAndWait, start: startRecording, capture } = recorder
  const startRef = useRef(startRecording)
  startRef.current = startRecording

  /**
   * 저장 성공(신규 행 1건 이상) 뒤 — 녹화를 멈춰 녹화본을 완성하고, 촬영분과 함께 저장된 행에 올린다. 업로드 실패는 저장을 되돌리지 않는다(안내만).
   * 첨부 대상(녹화본·촬영분)은 이 순간 잘라 내고 화면 상태는 곧바로 비운다 — 업로드가 끝난 뒤 비우면 그 사이 찍은 다음 건 촬영분까지 지웠다(LOGISPRD-14589).
   */
  const handleSaved = useCallback(
    (result: ReturnWorkScanSaveRes, savedShipperCode = shipperCode) => {
      const targetIds = result.inserted.map((row) => row.id)
      // 일부만 중복이면 송장 정보가 남아 자동녹화 트리거가 내려가지 않는다 — 멈춘 녹화를 여기서 다시 켠다(LOGISPRD-14589).
      const headKept = result.duplicateOrderNumbers.length > 0
      const recording = stopAndWait()
      // captures 는 [검수완료]를 누른 렌더의 값이다 — useReturnWorkScan.handleSave 가 그때의 onSaved 를 잡는다. onSaved 를 ref 로 바꾸면 응답 대기 중 찍은 촬영분이 이 건에 섞인다.
      discardRecorder(captures)
      recording
        .then((recordingBlob) => {
          const upload = uploadInspectionFiles({ shipperCode: savedShipperCode, targetIds, recordingBlob, captures })
          if (headKept) startRef.current()
          return upload
        })
        .catch(() => scanNotice.failure(MESSAGES.uploadFailed))
    },
    [shipperCode, captures, stopAndWait, discardRecorder],
  )

  const scan = useReturnWorkScan({ shipperCode, coMingleEnabled, filters, onSaved: handleSaved })

  // 자동녹화 트리거 — 송장 정보가 채워졌거나(원송장 조회 성공·주문검색 선택) 수기입력을 켰을 때. 꺼짐→켜짐 순간에만 시작한다.
  //  카메라 준비(권한 허용)도 조건에 넣는다 — 권한 창이 늦게 닫히면 그 사이 켜진 트리거가 스트림 없이 한 번 지나가 첫 건이 녹화되지 않았다(사전 리뷰 S2).
  const recordTrigger = recorder.isCameraReady && (scan.resolvedHead != null || scan.isManualEntry)
  useEffect(() => {
    if (recordTrigger) startRef.current()
  }, [recordTrigger])

  /** [촬영(캡처)] — 미리보기 영상의 한 장을 '파일' 목록에 쌓는다(검수완료 때 녹화본과 함께 올라간다). */
  const handleCapture = useCallback(() => {
    if (!recorder.isCameraReady) {
      scanNotice.invalid(MESSAGES.cameraNotReady)
      return
    }
    capture().catch(() => scanNotice.failure(MESSAGES.captureFailed))
  }, [recorder.isCameraReady, capture])

  // ── 스캔 입력 칸 값 — 훅 값이 바뀌면(주문검색 선택·저장 뒤 비움) 칸도 따라간다(옛 ScanInput draft 동기화) ──
  const [rtnDraft, setRtnDraft] = useState(scan.returnInvoiceNumber)
  const [orgDraft, setOrgDraft] = useState(scan.originalInvoiceNumber)
  const [barcodeDraft, setBarcodeDraft] = useState('')
  useEffect(() => setRtnDraft(scan.returnInvoiceNumber), [scan.returnInvoiceNumber])
  useEffect(() => setOrgDraft(scan.originalInvoiceNumber), [scan.originalInvoiceNumber])

  // ── 스캔 표 ──
  const rowsByKey = useMemo(() => new Map(scan.rows.map((r) => [r.key, r])), [scan.rows])
  const viewRows = useMemo(() => scan.rows.map(toViewRow), [scan.rows])

  // ── 주문검색 — 모달이 열려 있고 [조회]를 눌렀을 때만 찾는다(옛 모달: 닫으면 결과를 비운다) ──
  const [orderSearchOpen, setOrderSearchOpen] = useState(false)
  const [orderApplied, setOrderApplied] = useState<Record<string, unknown> | undefined>(undefined)
  const appliedRef = useRef<Record<string, unknown> | undefined>(undefined)
  const orderFilter = useMemo(() => (orderSearchOpen ? toOrderSearchFilter(orderApplied, shipperCode, dayjs(), coMingleEnabled) : null), [orderSearchOpen, orderApplied, shipperCode, coMingleEnabled])
  const orderQuery = useGetReturnOrderSearch(orderFilter)
  const { refetch: refetchOrders } = orderQuery
  useEffect(() => {
    if (!orderFilter) return
    // react-query v5 의 refetch 는 실패해도 reject 하지 않고 { error } 로 resolve 한다 — catch 로는 못 잡는다(#899 리뷰). 전역 알림은 쿼리 meta 로 끈다.
    refetchOrders()
      .then(({ error }) => {
        if (error) scanNotice.failure(MESSAGES.orderSearchFailed)
      })
      .catch(() => undefined) // refetch 는 reject 하지 않는다 — 실패는 위 then 이 알린다. 여기선 no-floating-promises 규칙만 닫는다
  }, [orderFilter, refetchOrders])
  const orderRows = useMemo(() => (orderFilter ? orderQuery.items.map((item) => toOrderViewRow(item, coMingleEnabled)) : []), [orderFilter, orderQuery.items, coMingleEnabled])
  const orderByKey = useMemo(() => new Map(orderQuery.items.map((r) => [orderRowKey(r), r])), [orderQuery.items])

  const manualValues: Record<string, string> = {
    [INVOICE_KEY]: scan.originalInvoiceNumber,
    ...Object.fromEntries(Object.entries(HEAD_FIELD_BY_KEY).map(([k, f]) => [k, scan.manualHead[f]])),
    [JOB_COMMENT_KEY]: scan.jobCommentForInvoice,
  }
  const resolvedDetails: Record<string, unknown> = scan.resolvedHead
    ? { [INVOICE_KEY]: scan.originalInvoiceNumber, ...Object.fromEntries(Object.entries(HEAD_FIELD_BY_KEY).map(([k, f]) => [k, scan.head[f]])) }
    : {}

  const wiring: Wiring = {
    permission: usePermissionSlot('rgr17'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr06WorkTable: coreTableLayoutSlot('RGR17_HEAD') },
    rows: {
      ROWS: () => ({ rows: viewRows, loading: scan.isItemLoading }),
      // 주문검색 후보 — 모달 [조회]가 적용한 조건이 오면 부모 상태로 옮겨 조회한다(이 슬롯은 뷰 렌더 중에 불리므로 상태 갱신은 렌더 뒤로 미룬다)
      //  모달이 닫혀 있을 땐 받지 않는다 — 뷰는 닫힌 동안에도 지난 적용 조건으로 이 슬롯을 부른다. 닫힌 동안 받으면 onModal 이 비운 조건이
      //  되살아나 다시 열 때 지난 조건으로 한 번 더 조회한다(LOGISPRD-14897, rgr11V2 와 같은 가드).
      ROWS2: (_demo, q) => {
        if (orderSearchOpen && appliedRef.current !== q.filters) {
          appliedRef.current = q.filters
          const next = q.filters
          queueMicrotask(() => setOrderApplied(next))
        }
        return { rows: orderRows, loading: orderQuery.isFetching }
      },
    },
    actions: {
      /** 검수완료 — 훅이 성공·실패를 알린다(false = 뷰 토스트 생략 — 바코드 칸 SAVE 로 저장해도 같은 안내가 나가게 훅이 한 곳에서 알린다) */
      rgr06InspectButton: async (): Promise<false> => {
        await scan.handleSave() // 진행 중 연타는 훅이 막는다
        return false
      },
      /** 반송장·원송장 Enter — 둘 다 있으면 HEAD 조회. 받았으면 뷰가 '송장 정보를 불러왔습니다.' 를 띄운다(막힘·0건·실패는 훅이 안내 → false) */
      rgr06RtnInput_enter: async (ctx) => ((await scan.scanReturnInvoice(ctx.value ?? '')) ? undefined : false),
      rgr06OrgInput_enter: async (ctx) => ((await scan.scanOriginalInvoice(ctx.value ?? '')) ? undefined : false),
      /** 상품바코드 Enter — ITEM 조회 뒤 표에 행 추가(SAVE 면 저장). 칸은 비운다(옛 WorkTable) */
      rgr06BarcodeInput_enter: (ctx) => {
        const v = (ctx.value ?? '').trim()
        setBarcodeDraft('')
        if (v) scan.handleBarcodeScan(v)
        return false
      },
      ROWS_rowsChange: (next) => scan.onRowsChange(fromViewRows(next, rowsByKey)),
      /** 주문검색 [선택] — 고른 주문을 송장 정보로(수취인 정보는 재조회하지 않는다). 뷰가 원송장 칸에 송장번호를 넣는다 */
      rgr06OrderSearchModal_ok: (ctx) => {
        const picked = ctx.picked?.[0]
        const res = picked ? orderByKey.get(text(picked.key)) : undefined
        if (!res) {
          throw scanInvalidError(MESSAGES.orderNotPicked)
        }
        if (scan.applyOrderSearchResult(res) === false) {
          throw scanInvalidError('스캔한 상품과 원본 화주가 다른 원주문입니다. 스캔한 상품을 저장하거나 초기화한 뒤 선택해 주세요.')
        }
        return MESSAGES.invoiceLoaded
      },
    },
    onModal: (open) => {
      setOrderSearchOpen(open.rgr06OrderSearchModal)
      // 닫으면 적용 조건도 비운다 — 안 비우면 다시 열 때 첫 렌더가 지난 조건으로 **같은 조회를 한 번 더** 보내고 지난 결과를 잠깐 그린다
      //  (뷰는 열린 뒤에야 적용 조건을 비운다). 옛 OrderSearchModal.handleClose 의 clearSearch 와 같은 자리(사전 리뷰 S4).
      if (!open.rgr06OrderSearchModal) {
        appliedRef.current = undefined
        setOrderApplied(undefined)
      }
    },
    options: {
      MASTERFLAGSELECT_OPTIONS: () => MASTER_FLAG_VIEW_OPTIONS,
      ROWS_ETC5: () => CONDITION_VIEW_OPTIONS,
    },
    fields: {
      rgr06RtnInput: () => ({ value: rtnDraft, onChange: setRtnDraft }),
      rgr06OrgInput: () => ({ value: orgDraft, onChange: setOrgDraft }),
      rgr06BarcodeInput: () => ({ value: barcodeDraft, onChange: setBarcodeDraft }),
      rgr06ManualSwitch: () => ({ value: scan.isManualEntry, onChange: scan.setIsManualEntry }),
      rgr06MasterFlag: () => ({
        value: filters.masterFlag,
        onChange: (v) => updateFilters({ ...filters, masterFlag: v === 'Y' ? 'Y' : 'N' }),
      }),
      // 수기입력 칸 — 송장은 원송장 값(저장 payload 의 originalInvoiceNumber), 수기입력사유는 jobCommentForInvoice, 나머지는 수기 HEAD
      rgr06InvoiceInfo: () => ({
        value: manualValues,
        onChange: (key, v) => {
          if (key === INVOICE_KEY) scan.setOriginalInvoiceNumber(v)
          else if (key === JOB_COMMENT_KEY) scan.setJobCommentForInvoice(v)
          else if (HEAD_FIELD_BY_KEY[key]) scan.setManualHead({ ...scan.manualHead, [HEAD_FIELD_BY_KEY[key]]: v })
        },
      }),
    },
    // 송장 정보 — 조회(또는 주문검색)로 받은 HEAD. 받기 전엔 비워 둔다('-')
    details: { rgr06InvoiceInfo: () => resolvedDetails },
    // 검수 녹화 — 생성본이 DS 요소와 같은 모양으로 그리고, 미리보기·녹화 표시·촬영 목록은 녹화 훅이 쥔다(DS 수정 없이, 2026-10-01).
    //  미리보기 <video> 는 카메라가 준비된 뒤에만 그린다 — 그 전엔 칸에 '연결 중'/'사용할 수 없음' 문구.
    recording: {
      rgr06Recording: () => ({
        preview: recorder.isCameraReady ? (
          <video
            ref={recorder.attachVideo}
            autoPlay
            muted
            playsInline
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : undefined,
        recording: recorder.isRecording,
        caption: recorder.isCameraUnavailable ? MESSAGES.cameraUnavailable : MESSAGES.cameraConnecting,
        files: recorder.captureNames,
        onCapture: handleCapture,
        onRemoveFile: recorder.removeCapture,
      }),
    },
  }

  return <ReturnWorkPageView wiring={wiring} />
}
